import React, { lazy, Suspense, useState } from 'react';
import { Spin } from 'Components';
import Heading from 'LearningManager/components/Heading';
import ReportActions from 'LearningManager/components/ReportActions';
import styles from 'LearningManager/styles/TrainingReports.module.scss';
import ReportFilters from './components/ReportFilters';
import ViewToggle from './components/ViewToggle';
import ReportTabs from './Table/ReportTabs';
import {
	DEFAULT_TABLE_TAB,
	REPORT_TABS
} from './Table/TrainingTable.constants';
import { ReportFiltersProvider } from './context/ReportFilters.context';
import { ReportTableTab, ReportView } from './TrainingReports.types';
import {
	DEFAULT_REPORT_VIEW,
	PLACEHOLDER_FILTER_OPTIONS,
	REPORT_SUBTITLE,
	REPORT_TITLE,
	SEARCH_PLACEHOLDER
} from './TrainingReports.constants';

const Dashboard = lazy(() => import('./Dashboard/Dashboard'));
const TrainingTable = lazy(() => import('./Table/TrainingTable'));

const TrainingReports = () => {
	const [view, setView] = useState<ReportView>(DEFAULT_REPORT_VIEW);
	const [tableTab, setTableTab] = useState<ReportTableTab>(DEFAULT_TABLE_TAB);

	const isTableView = view === ReportView.Report;

	const searchPlaceholder = isTableView
		? REPORT_TABS.find((tab) => tab.key === tableTab)?.searchPlaceholder ??
		  SEARCH_PLACEHOLDER
		: SEARCH_PLACEHOLDER;

	return (
		<ReportFiltersProvider>
			<div className={styles.page}>
				<TopSection view={view} onViewChange={setView} />

				{isTableView && (
					<ReportTabs value={tableTab} onChange={setTableTab} />
				)}

				<div className={styles.filterBar}>
					<ReportFilters
						className={styles.filters}
						options={PLACEHOLDER_FILTER_OPTIONS}
						searchPlaceholder={searchPlaceholder}
					/>
					<ReportActions className={styles.filterActions} />
				</div>

				<Suspense fallback={<ViewFallback />}>
					{isTableView ? (
						<TrainingTable tab={tableTab} />
					) : (
						<Dashboard />
					)}
				</Suspense>
			</div>
		</ReportFiltersProvider>
	);
};

interface TopSectionProps {
	view: ReportView;
	onViewChange: (view: ReportView) => void;
}

const TopSection: React.FC<TopSectionProps> = ({ view, onViewChange }) => {
	return (
		<div className={styles.heading}>
			<Heading
				titleClassName={styles.title}
				title={REPORT_TITLE}
				subTitleClassName={styles.subtitle}
				subHeading={REPORT_SUBTITLE}
			/>
			<ViewToggle value={view} onChange={onViewChange} />
		</div>
	);
};

const ViewFallback = () => (
	<div className={styles.viewFallback}>
		<Spin size="large" />
	</div>
);

export default TrainingReports;
